import { usePermissionContext } from "@/shared/hooks/usePermissionContext";
import { PermissionAction, PermissionResource } from "@/entities/user";

export const usePermission = () => {
  const {
    hasPermission,
    isSuperAdmin,
    userRole,
    profileData
  } = usePermissionContext();

  const can = (resource: PermissionResource, action: PermissionAction): boolean =>
    hasPermission(resource, action);

  const canAny = (resource: PermissionResource, actions: PermissionAction[]): boolean =>
    actions.some(action => hasPermission(resource, action));

  const canAll = (resource: PermissionResource, actions: PermissionAction[]): boolean =>
    actions.every(action => hasPermission(resource, action));

  const canRead = (resource: PermissionResource): boolean =>
    hasPermission(resource, 'read');

  const canCreate = (resource: PermissionResource): boolean =>
    hasPermission(resource, 'create');

  const canUpdate = (resource: PermissionResource): boolean =>
    hasPermission(resource, 'update');

  const canDelete = (resource: PermissionResource): boolean =>
    hasPermission(resource, 'delete');

  const hasRole = (role: string): boolean => {
    if (isSuperAdmin) return true;
    return userRole === role;
  };

  const hasAnyRole = (roles: string[]): boolean => {
    if (isSuperAdmin) return true;
    return userRole ? roles.includes(userRole) : false;
  };

  return {
    can,
    canAny,
    canAll,
    canRead,
    canCreate,
    canUpdate,
    canDelete,
    hasRole,
    hasAnyRole,
    isSuperAdmin,
    userRole,
    profileData,
  };
};

export default usePermission;
